import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Crown, Check, AlertCircle, Calendar, Loader2, Sparkles, Image as ImageIcon, ListChecks, Target, BookOpen, Brain, Wallet, Infinity as InfinityIcon, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_META = {
  active: { label: 'Activa', color: 'success', icon: Check },
  pending: { label: 'Pago en proceso', color: 'warning', icon: Loader2 },
  failed: { label: 'Pago rechazado', color: 'error', icon: AlertCircle },
  cancelled: { label: 'Cancelada', color: 'textMuted', icon: AlertCircle },
  expired: { label: 'Vencida', color: 'error', icon: AlertCircle },
  none: { label: 'Sin plan', color: 'textMuted', icon: Crown },
};

// Render value or ∞
const fmtLimit = (v) => (v === null || v === undefined ? '∞' : v);
const isUnl = (v) => v === null || v === undefined;

// Build the list of feature rows shown on each pricing card.
// Pulled directly from the backend `limits` object for that plan.
const buildFeatureRows = (planId, credits, limits) => {
  const rows = [
    {
      icon: Sparkles,
      label: 'Créditos de IA',
      value: isUnl(credits) ? 'Ilimitados' : `${credits} / período`,
      strong: true,
      unlocked: true,
    },
    {
      icon: ImageIcon,
      label: 'Tableros de visión',
      value: isUnl(limits.vision_boards) ? 'Ilimitados' : `${limits.vision_boards} tablero${limits.vision_boards === 1 ? '' : 's'}`,
      unlocked: true,
    },
    {
      icon: ListChecks,
      label: 'Hábitos activos',
      value: isUnl(limits.habits) ? 'Ilimitados' : `${limits.habits} hábitos`,
      unlocked: true,
    },
    {
      icon: Target,
      label: 'Metas (con IA)',
      value: `${isUnl(limits.goals) ? '∞' : limits.goals} metas · ${isUnl(limits.goals_with_ai) ? '∞' : limits.goals_with_ai} con IA`,
      unlocked: true,
    },
    {
      icon: BookOpen,
      label: 'Diarios',
      value: isUnl(limits.journals)
        ? 'Ilimitados'
        : `${limits.journals} diario${limits.journals === 1 ? '' : 's'}${isUnl(limits.journal_ai_questions) ? '' : ` · ${limits.journal_ai_questions} reflexiones IA`}`,
      unlocked: true,
    },
    {
      icon: Brain,
      label: 'Claridad mental',
      value: isUnl(limits.clarity_ideas) ? 'Ideas ilimitadas' : `${limits.clarity_ideas} ideas`,
      unlocked: true,
    },
    {
      icon: Wallet,
      label: 'Presupuesto',
      value: limits.budget_access ? 'Desbloqueado' : 'Bloqueado',
      unlocked: !!limits.budget_access,
      strong: !!limits.budget_access,
    },
  ];
  return rows;
};

const Membership = () => {
  const { theme } = useTheme();
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [env, setEnv] = useState('sandbox');
  const [configured, setConfigured] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [plansDeactivated, setPlansDeactivated] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [plansR, subR] = await Promise.all([
        api.get('/memberships/plans'),
        api.get('/memberships/me').catch(() => ({ data: { status: 'none' } })),
      ]);
      setPlans(plansR.data?.plans || []);
      setEnv(plansR.data?.env || 'sandbox');
      setConfigured(plansR.data?.configured !== false);
      setPlansDeactivated(!!plansR.data?.plans_deactivated);
      setSubscription(subR.data);
    } catch (err) {
      console.warn('membership load failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    const status = searchParams.get('status');
    if (status === 'success') {
      toast.success('¡Pago iniciado! Tu plan se activará al confirmar el pago.');
      setTimeout(() => { loadAll(); refreshUser?.(); }, 1500);
      setSearchParams({}, { replace: true });
    } else if (status === 'cancelled') {
      toast.info('Pago cancelado');
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = async (planId) => {
    setSubscribing(planId);

    try {
      const { data } = await api.post('/memberships/subscribe', {
        plan_id: planId,
      });

      if (data?.subscription_url) {
        window.location.href = data.subscription_url;
      } else {
        toast.error('No se recibió URL de suscripción');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(msg || 'Error al iniciar la suscripción');
    } finally {
      setSubscribing(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const statusKey = subscription?.status || 'none';
  const meta = STATUS_META[statusKey] || STATUS_META.none;
  const StatusIcon = meta.icon;
  const isActive = statusKey === 'active' || statusKey === 'pending';

  // Order plans for display: Free → Light → Pro → Premium → Trimestral
  const PLAN_ORDER = ['free', 'light', 'pro', 'vip', 'trimestral'];
  const orderedPlans = [...plans].sort(
    (a, b) => PLAN_ORDER.indexOf(a.id) - PLAN_ORDER.indexOf(b.id)
  );

  const PLAN_TAGLINES = {
    free: 'Plan inicial · Gratis para siempre',
    light: 'Para mantener el ritmo',
    pro: 'Para trabajar mejor',
    vip: 'Sin límites · Presupuesto incluido',
    trimestral: 'Mejor valor a largo plazo',
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-8" data-testid="membership-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Crown size={18} style={{ color: theme.accent }} />
          <p className="text-xs uppercase tracking-[0.25em]" style={{ color: theme.textSecondary }}>Plan & Pago</p>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight" style={{ color: theme.accent }}>Membresía</h1>
        <p className="text-sm mt-2 max-w-xl" style={{ color: theme.textSecondary }}>
          Elegí el plan que acompaña tu ritmo. Todos los planes incluyen Vision Board, Hábitos, Metas, Calendario, Diario y Claridad Mental — sólo cambia el techo.
        </p>
      </div>

      {!configured && (
        <div className="rounded-xl p-4 flex items-center gap-3"
          style={{ background: `${theme.warning}15`, border: `1px solid ${theme.warning}50`, color: theme.warning }}>
          <AlertCircle size={18} />
          <div>
            <p className="text-sm font-semibold">Pagos en configuración</p>
            <p className="text-[11px]">La pasarela de pagos aún no está conectada (faltan credenciales en backend). Mientras tanto la página muestra el catálogo pero no procesará pagos reales.</p>
          </div>
        </div>
      )}

      {/* Current subscription card */}
      <div className="rounded-2xl p-6" style={{ background: theme.card, border: `1px solid ${theme.border}` }} data-testid="membership-current">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: theme.textMuted }}>Tu plan actual</p>
            <div className="flex items-center gap-3">
              <h2 className="text-3xl font-light" style={{ color: theme.textPrimary }}>
                {isActive && subscription?.plan_id
                  ? (plans.find(p => p.id === subscription.plan_id)?.name || subscription.plan_id)
                  : 'Free'}
              </h2>
              <span className="flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                style={{ background: `${theme[meta.color] || theme.textMuted}20`, color: theme[meta.color] || theme.textMuted }}>
                <StatusIcon size={12} className={statusKey === 'pending' ? 'animate-spin' : ''} />{meta.label}
              </span>
            </div>
            {subscription?.next_renewal && isActive && (
              <p className="text-xs mt-2 flex items-center gap-1.5" style={{ color: theme.textMuted }}>
                <Calendar size={12} />Acceso hasta: <span style={{ color: theme.textPrimary }}>{formatDate(subscription.next_renewal)}</span>
              </p>
            )}
            {subscription?.transaction_id && (
              <p className="text-[10px] mt-1" style={{ color: theme.textMuted }}>
                Transacción: <span className="font-mono">{subscription.transaction_id}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {plansDeactivated && (
        <div className="rounded-xl p-4 flex items-start gap-3"
          style={{ background: `${theme.accent}15`, border: `1px solid ${theme.accent}50`, color: theme.accent }}>
          <Sparkles size={18} />
          <div>
            <p className="text-sm font-semibold" style={{ color: theme.textPrimary }}>Pagos pausados temporalmente</p>
            <p className="text-[11px]" style={{ color: theme.textSecondary }}>
              Estamos en período de pruebas: <strong>todas las funciones están desbloqueadas</strong> sin costo para que pruebes la app entera. Los planes vuelven a estar disponibles pronto.
            </p>
          </div>
        </div>
      )}

      {/* Plans grid */}
      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <p className="text-[10px] uppercase tracking-widest" style={{ color: theme.textMuted }}>
            Planes disponibles
            {env === 'sandbox' && (
              <span className="ml-2 px-1.5 py-0.5 rounded text-[10px]" style={{ background: `${theme.warning}30`, color: theme.warning }}>SANDBOX</span>
            )}
          </p>
          <p className="text-[10px]" style={{ color: theme.textMuted }}>
            Solo <span style={{ color: theme.accent, fontWeight: 600 }}>Premium</span> desbloquea Presupuesto.
          </p>
        </div>

        {loading ? (
          <p className="text-center py-12 text-sm" style={{ color: theme.textMuted }}>Cargando planes...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 auto-rows-fr max-w-5xl mx-auto">
            {orderedPlans.map(plan => {
              const isCurrent = subscription?.plan_id === plan.id && isActive;
              const isPro = plan.id === 'vip';
              const isFree = plan.id === 'free';
              const features = buildFeatureRows(plan.id, plan.credits_monthly, plan.limits || {});
              const tagline = PLAN_TAGLINES[plan.id] || '';
              const period = plan.frequency_value > 1 ? `${plan.frequency_value} meses` : 'mes';

              return (
                <div key={plan.id}
                  className="relative rounded-3xl p-6 sm:p-7 flex flex-col h-full transition-all hover:scale-[1.02] hover:-translate-y-1"
                  style={{
                    background: isPro
                      ? `linear-gradient(165deg, ${theme.primary}35, ${theme.card} 55%)`
                      : `linear-gradient(180deg, ${theme.card}, ${theme.elevated}80)`,
                    border: `1px solid ${isCurrent ? theme.success : (isPro ? theme.accent : theme.border)}`,
                    boxShadow: isPro
                      ? `0 20px 60px ${theme.primary}40, 0 0 0 1px ${theme.accent}30 inset`
                      : `0 4px 20px rgba(0,0,0,0.15)`,
                  }}
                  data-testid={`plan-${plan.id}`}>
                  {isPro && (
                    <span className="absolute -top-3 right-5 px-3 py-1 rounded-full text-[9px] uppercase tracking-widest font-bold flex items-center gap-1"
                      style={{ background: theme.accent, color: theme.bg, boxShadow: `0 6px 20px ${theme.accent}60` }}>
                      <Crown size={11} /> Más popular
                    </span>
                  )}

                  {/* Header */}
                  <div className="mb-4">
                    <div className="flex items-baseline justify-between gap-2">
                      <h3 className="text-2xl sm:text-3xl font-medium tracking-tight" style={{ color: theme.textPrimary }}>{plan.name}</h3>
                      {isPro && <InfinityIcon size={20} style={{ color: theme.accent }} />}
                    </div>
                    <p className="text-xs mt-1.5 leading-relaxed" style={{ color: theme.textMuted }}>{tagline}</p>
                  </div>

                  {/* Price */}
                  <div className="mb-6 pb-5" style={{ borderBottom: `1px solid ${theme.border}80` }}>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xl sm:text-2xl font-medium" style={{ color: theme.textMuted }}>Gs.</span>
                      <span className="text-5xl sm:text-6xl font-light tracking-tight tabular-nums" style={{ color: theme.accent }}>
                        {Number(plan.price).toLocaleString('es-PY')}
                      </span>
                      <span className="text-xs ml-1" style={{ color: theme.textMuted }}>/ {period}</span>
                    </div>
                    {plan.credits_monthly !== undefined && (
                      <p className="text-[11px] mt-2" style={{ color: theme.textMuted }}>
                        {isUnl(plan.credits_monthly) ? 'Créditos IA ilimitados' : `${plan.credits_monthly} créditos IA / ${period}`}
                      </p>
                    )}
                  </div>

                  {/* Feature list */}
                  <ul className="space-y-3.5 mb-6 flex-1">
                    {features.map((f, i) => {
                      const FIcon = f.icon;
                      const muted = !f.unlocked;
                      return (
                        <li key={i} className="flex items-start gap-3 text-[13px] leading-snug">
                          <span className="mt-0.5 shrink-0">
                            {muted
                              ? <Lock size={15} style={{ color: theme.textMuted, opacity: 0.55 }} />
                              : <FIcon size={15} style={{ color: f.strong ? theme.accent : theme.success }} />
                            }
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block font-medium" style={{ color: muted ? theme.textMuted : theme.textPrimary, textDecoration: muted ? 'line-through' : 'none' }}>{f.label}</span>
                            <span className="block text-[11.5px] mt-1 leading-snug" style={{ color: muted ? theme.textMuted : (f.strong ? theme.accent : theme.textSecondary), fontWeight: f.strong ? 600 : 400 }}>{f.value}</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  {/* CTA */}
                  {isFree ? (
                    <Button disabled className="w-full h-12" style={{ background: theme.elevated, color: theme.textMuted, border: `1px solid ${theme.border}` }}>
                      Plan inicial
                    </Button>
                  ) : isCurrent ? (
                    <Button disabled className="w-full h-12" style={{ background: theme.success, color: '#fff', opacity: 0.9 }} data-testid={`current-${plan.id}`}>
                      <Check size={14} className="mr-1.5" />Tu plan
                    </Button>
                  ) : plansDeactivated ? (
                    <Button disabled className="w-full h-12"
                      style={{ background: theme.elevated, color: theme.textMuted, border: `1px dashed ${theme.border}` }}
                      data-testid={`subscribe-${plan.id}`}>
                      Próximamente
                    </Button>
                  ) : (
                    <Button onClick={() => subscribe(plan.id)} disabled={!!subscribing || !configured}
                      className="w-full h-12 font-medium"
                      style={{ background: isPro ? theme.accent : theme.primary, color: isPro ? theme.bg : '#fff' }}
                      data-testid={`subscribe-${plan.id}`}>
                      {subscribing === plan.id ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                      {subscribing === plan.id ? 'Redirigiendo...' : 'Suscribirme'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-center text-[11px]" style={{ color: theme.textMuted }}>
        Pagos procesados de forma segura por nuestro proveedor de pagos autorizado · Suscripciones mensuales gestionadas por PagoPar · Tus datos de pago nunca pasan por nuestros servidores
      </p>
    </div>
  );
};

export default Membership;
